import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
  serializeJobSkillsForCompare,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { JobSkillsManagerBody } from './employer/job-skills-manager-body'
import type { JobSkillDraft } from './employer/job-skill-manager-row'
import {
  type OrganisationOpportunity,
  useOrganisationOpportunities,
  useOpportunity,
  useOpportunityMutations,
  type OrganisationOpportunitySkillData,
  managedOpportunityStructuredFields,
} from '../hooks/use-organisation-opportunities'
import { JOB_SKILLS_CANVAS_FIELDS, opportunityCanvasSurface } from '../lib/canvas-ui-surface'
import { nextJobSkillDrafts } from '../lib/job-skills-draft'

interface OpportunitySkillsCanvasProps {
  organisationSlug?: string
  opportunityId?: string
  opportunity: OrganisationOpportunity
  onClose: () => void
  onSaved?: (updated: OrganisationOpportunity) => void
}

export function OpportunitySkillsCanvas({
  organisationSlug = '',
  opportunityId,
  opportunity,
  onClose,
  onSaved,
}: OpportunitySkillsCanvasProps) {
  const { setCanvasSurface } = useArtifactCanvas()
  const orgMutations = useOrganisationOpportunities(organisationSlug)
  const workspaceMutations = useOpportunityMutations(opportunityId ?? opportunity.id)
  const useSlugFreeUpdate = Boolean(opportunityId)
  const isUpdating = useSlugFreeUpdate ? workspaceMutations.isUpdating : orgMutations.isUpdating
  const updateError = useSlugFreeUpdate ? workspaceMutations.updateError : orgMutations.updateError

  const lastSkillsSerializedRef = useRef('')
  const [skills, setSkills] = useState<JobSkillDraft[]>([])
  const surfaceId = opportunityId ?? opportunity.id
  const live = useOpportunity(surfaceId)
  const current = live.data ?? opportunity

  useLayoutEffect(() => {
    lastSkillsSerializedRef.current = ''
  }, [opportunity.id])

  useLayoutEffect(() => {
    setCanvasSurface(opportunityCanvasSurface(surfaceId, [...JOB_SKILLS_CANVAS_FIELDS]))
  }, [setCanvasSurface, surfaceId])

  useLayoutEffect(() => {
    const next = nextJobSkillDrafts(current.skills, lastSkillsSerializedRef.current)
    lastSkillsSerializedRef.current = next.serialized
    if (next.drafts) setSkills(next.drafts)
  }, [current])

  const baselineSerialized = useMemo(
    () => serializeJobSkillsForCompare(current.skills),
    [current.skills]
  )
  const draftSerialized = useMemo(() => serializeJobSkillsForCompare(skills), [skills])
  const isDirty = baselineSerialized !== draftSerialized

  const remoteErr =
    updateError instanceof Error ? updateError.message : updateError ? String(updateError) : null

  const handleSave = () => {
    const payload: OrganisationOpportunitySkillData[] = skills.map((s) => ({
      skill_id: s.skill_id,
      requirement_type: s.requirement_type,
    }))
    const data = {
      title: current.title,
      description: current.description,
      skills: payload,
      ...managedOpportunityStructuredFields(current),
    }
    const savePromise = useSlugFreeUpdate
      ? workspaceMutations.updateOpportunity(data)
      : orgMutations.updateOpportunity({ id: current.id, data })
    void savePromise
      .then((updated) => {
        onSaved?.(updated)
        onClose()
      })
      .catch(() => {
        // Server error surfaces via updateError
      })
  }

  const slug = organisationSlug || current.organisation_slug || ''

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <JobSkillsManagerBody
          skills={skills}
          onSkillsChange={setSkills}
          organisationSlug={slug}
          disabled={isUpdating}
        />
      </div>

      <ArtifactCanvasInlineError>{remoteErr}</ArtifactCanvasInlineError>

      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isUpdating}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
